import React, { useEffect, useMemo, useState } from "react";
import z from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Select, Modal, NumberString } from "@/components";
import isEmpty from "lodash/isEmpty";
import { useShopStore } from "@/store/shopStore";
import { toastError, toastSuccess } from "@/utils/toastify";
import {
  ApiResponse,
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_SUCCESS_MESSAGE,
  GetStoresResp,
  PriorityRole,
  RoleEnum,
} from "@/types";
import { useLoadingStore } from "@/store/loadingStore";
import { useGroupStore } from "@/store/groupStore";
import { cn } from "@/utils";
import { RiPencilFill } from "react-icons/ri";
import { useCompanyStore } from "@/store/companyStore";
import { useAuth } from "@/contexts/AuthContext";
import { phoneNumberOptionalValidation } from "../company/schema";

type EditStoreFormData = {
  name: string;
  phone?: string;
  group?: number | null;
  address?: string;
  report_history?: string;
  store_inspection_history?: string;
};

const LIMIT = 20;
const SPACE_LETTER = " ";

const schema = z.object({
  name: z
    .string(SPACE_LETTER)
    .trim()
    .min(1, SPACE_LETTER)
    .max(255, "会社名は半角255文字以内で入力してください。"),
  group: z.number().nullable().optional(),
  address: z
    .string(SPACE_LETTER)
    .max(255, "会社名は半角255文字以内で入力してください。")
    .optional(),
  phone: phoneNumberOptionalValidation,
  report_history: z
    .string()
    .trim()
    .max(1000, "報告履歴は半角1000文字以内で入力してください。")
    .optional(),
  store_inspection_history: z
    .string()
    .trim()
    .max(1000, "随時点検履歴は半角1000文字以内で入力してください。")
    .optional(),
});

export const EditStore = React.memo(function EditStore({
  id,
  defaultValues,
  children,
  className,
  disabled,
}: {
  id: number;
  defaultValues?: EditStoreFormData;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { setLoading } = useLoadingStore();
  const [loadingGroupsInCompany, setLoadingGroupsInCompany] = useState(false);

  const { updateShop, shop } = useShopStore();
  const { groupsInCompany: groupsInCompanyStore, fetchGroupsInCompany } =
    useGroupStore();
  const { stores, setStores } = useCompanyStore();
  const { role, user } = useAuth();

  // Store manager can edit store without group
  const groupOptionDefault = {
    data: [
      {
        label: shop?.group?.name || "",
        value: shop?.group_id || null,
      },
    ],
    pagination: {
      page: 1,
    },
  } as unknown as ApiResponse<{ label: string; value: number }[]>;

  const groupsInCompany =
    PriorityRole[role || RoleEnum.OTHER] <
    PriorityRole[RoleEnum.HEAD_OFFICE_USER]
      ? groupOptionDefault
      : groupsInCompanyStore;

  const companyId = Number(user?.company_id);
  const page = groupsInCompany.pagination?.page || 1;
  const hasMore = false; // Update loading inscroll sang load hết

  const storeDisplayName = useMemo(() => {
    return shop?.company?.store_display_name || "店舗";
  }, [shop]);

  useEffect(() => {
    if (isEmpty(groupsInCompany.data)) {
      loadOptions(1);
    }
  }, [companyId]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<EditStoreFormData>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues || {
      name: shop?.name || "",
      group: shop?.group_id,
      address: shop?.address || "",
      phone: shop?.phone || "",
      report_history: shop?.report_history || "",
      store_inspection_history: shop?.store_inspection_history || "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (!defaultValues) return;
    reset(defaultValues);
  }, [defaultValues]);

  const loadOptions = async (page: number) => {
    if (loadingGroupsInCompany || !companyId) return;
    setLoadingGroupsInCompany(true);
    if (
      PriorityRole[role || RoleEnum.OTHER] <
      PriorityRole[RoleEnum.HEAD_OFFICE_USER]
    ) {
      // Login with permisstion of user < head, do not fetch group
      setLoadingGroupsInCompany(false);
      return;
    }
    await fetchGroupsInCompany(role || RoleEnum.HEAD_OFFICE_USER, {
      companyId: companyId,
      page: page,
      limit: LIMIT,
      isPagination: 0, // update loading inscroll sang load hết
    });
    setLoadingGroupsInCompany(false);
  };

  const handleScrollToBottom = () => {
    if (hasMore) {
      const nextPage = page + 1;
      loadOptions(nextPage);
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit = async (data: EditStoreFormData) => {
    setLoading(true);
    const oldStore = stores;
    try {
      const newStores = {
        ...stores,
        data:
          stores?.data.map((i) =>
            i.id === id
              ? {
                  id: i.id,
                  name: data.name?.trim(),
                  group: {
                    id: data.group,
                    name:
                      groupsInCompany.data?.find((g) => g.value === data.group)
                        ?.label || "",
                  },
                  address: data.address?.trim() || "",
                  status: i.status,
                  phone: data.phone || "",
                  report_history: data.report_history?.trim() || "",
                  store_inspection_history:
                    data.store_inspection_history?.trim() || "",
                }
              : i
          ) || [],
      } as unknown as ApiResponse<GetStoresResp[]>;
      setStores(newStores);
      const resp = await updateShop(id, {
        name: data.name?.trim(),
        group_id: data.group ? +data.group : null,
        address: data.address?.trim() ?? "",
        phone: data.phone,
        report_history: data.report_history?.trim() || "",
        store_inspection_history: data.store_inspection_history?.trim() || "",
      });
      toastSuccess(resp.message || DEFAULT_SUCCESS_MESSAGE);
    } catch (error: any) {
      setStores(oldStore);
      toastError(
        error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE
      );
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  return (
    <>
      <div
        onClick={(e) => {
          if (disabled) return;
          e.stopPropagation();
          handleOpen();
        }}
        className={cn(
          disabled ? "opacity-50 !cursor-not-allowed" : "",
          className
        )}
      >
        {children || (
          <RiPencilFill
            className={cn(
              "w-5 h-5 text-grey-600/80 hover:text-cyan",
              disabled
                ? "opacity-80 !cursor-not-allowed hover:!text-grey-600/80"
                : ""
            )}
          />
        )}
      </div>
      <Modal
        title={`${storeDisplayName}情報編集`}
        open={open}
        onOpenChange={handleClose}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label={`${storeDisplayName}名`}
              placeholder={`${storeDisplayName}名を入力してください。`}
              required
              {...register("name")}
              containerClassName="col-span-2"
              error={errors.name?.message}
              maxLength={150}
            />
            <Controller
              control={control}
              name="group"
              render={({ field: { onChange, value } }) => {
                const selectedGroup = value
                  ? groupsInCompany.data.find((i) => i.value === +value) || []
                  : [];
                return (
                  <Select
                    label="グループ"
                    isDisabled={
                      PriorityRole[role || RoleEnum.OTHER] <=
                      PriorityRole[RoleEnum.STORE_MANAGER]
                    }
                    value={selectedGroup}
                    onChange={(item) => {
                      onChange(item?.value || null);
                    }}
                    options={groupsInCompany.data}
                    isLoading={loadingGroupsInCompany}
                    className="col-span-2"
                    isRelativeMenu={true}
                    onMenuScrollToBottom={handleScrollToBottom}
                  />
                );
              }}
            />
            <Input
              label="住所"
              placeholder="住所を入力してください。"
              {...register("address")}
              containerClassName="col-span-2"
            />

            <Controller
              control={control}
              name="phone"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <NumberString
                  label="電話番号"
                  name="phone"
                  placeholder="電話番号を入力してください。"
                  containerClassName="col-span-2"
                  error={error?.message ?? ""}
                  value={value || ""}
                  onChange={onChange}
                  maxLength={255}
                  allowHyphen
                />
              )}
            />
            <Input
              label="申告履歴"
              placeholder="申告履歴を入力してください。"
              containerClassName="col-span-2"
              {...register("report_history")}
            />
            <Input
              label="随時点検履歴"
              placeholder="随時点検履歴を入力してください。"
              containerClassName="col-span-2"
              {...register("store_inspection_history")}
            />
          </div>
          <div className="flex justify-end gap-4 mt-4">
            <Button
              buttonType="text"
              className="!h-12"
              type="button"
              onClick={handleClose}
            >
              キャンセル
            </Button>
            <Button
              buttonType="primary"
              className="!h-12"
              type="submit"
              disabled={!isEmpty(errors)}
            >
              更新する
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
});
